import { Box, Typography, Chip, Tooltip } from '@mui/material';
import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Customer } from './types/type';
import { formatNumber, useLocale } from '../../utils/formatNumber';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';
import { convertWeightKGtoLB } from '../../utils/convertWeight';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SwapVertIcon from '@mui/icons-material/SwapVert';

export interface Shipment {
  id: string;
  status: string;
  account: string;
}

interface TopTenViewTableProps {
  data: Customer[];
  setCustomerShipmentSort?: any;
  setCustomerWeightSort?: any;
  setCustomerRevenueSort?: any;
  customerRevenueSort: boolean;
  customerWeightSort: boolean;
  customerShipmentSort: boolean;
}

const TopTenViewTable: React.FC<TopTenViewTableProps> = ({
  data,
  setCustomerShipmentSort,
  setCustomerRevenueSort,
  setCustomerWeightSort,
  customerWeightSort,
  customerShipmentSort,
  customerRevenueSort,
}) => {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const handleShipmentSort = () => {
    setCustomerShipmentSort((prev: any) => !prev);
    setCustomerWeightSort(false);
    setCustomerRevenueSort(false);
  };

  const handleWeightSort = () => {
    setCustomerWeightSort((prev: any) => !prev);
    setCustomerShipmentSort(false);
    setCustomerRevenueSort(false);
  };

  const handleRevenueSort = () => {
    setCustomerRevenueSort((prev: any) => !prev);
    setCustomerWeightSort(false);
    setCustomerShipmentSort(false);
  };

  const locale = useLocale();

  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const getPercentageColor = (percentage: number) => {
    if (percentage >= 10) return { bg: '#E8F5E9', text: '#2E7D32' };
    if (percentage >= 0) return { bg: '#E3F2FD', text: '#1976D2' };
    if (percentage >= -10) return { bg: '#FFF3E0', text: '#F57C00' };
    return { bg: '#FFEBEE', text: '#C62828' };
  };

  const MetricCard: React.FC<{
    value: number;
    percentage: number;
    label?: string;
  }> = ({ value, percentage, label }) => {
    const colors = getPercentageColor(percentage);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#181D25',
            fontFamily: 'Poppins',
          }}
        >
          {label || formatNumber(value, locale)}
        </Typography>
        <Chip
          icon={percentage >= 0 ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
          label={`${formatNumber(Math.abs(percentage), locale)}%`}
          size="small"
          sx={{
            height: '24px',
            fontSize: '12px',
            fontWeight: 600,
            fontFamily: 'Poppins',
            bgcolor: colors.bg,
            color: colors.text,
            border: 'none',
            '& .MuiChip-icon': {
              color: colors.text,
              marginLeft: '6px',
            },
          }}
        />
      </Box>
    );
  };

  const SortableHeader: React.FC<{
    label: string;
    active: boolean;
    onClick: () => void;
    align?: 'left' | 'center' | 'right';
  }> = ({ label, active, onClick, align = 'center' }) => (
    <TableCell
      onClick={onClick}
      sx={{
        fontFamily: 'Poppins',
        fontWeight: active ? 600 : 500,
        fontSize: '14px',
        color: active ? '#1976D2' : '#606E80',
        cursor: 'pointer',
        textAlign: align,
        borderBottom: '2px solid #E0E0E0',
        bgcolor: active ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
        transition: 'all 0.2s',
        userSelect: 'none',
        '&:hover': {
          bgcolor: 'rgba(25, 118, 210, 0.08)',
          color: '#1976D2',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align, gap: 0.5 }}>
        {label}
        <SwapVertIcon
          sx={{
            fontSize: 18,
            opacity: active ? 1 : 0.4,
            transition: 'opacity 0.2s',
          }}
        />
      </Box>
    </TableCell>
  );

  return (
    <Box sx={{ padding: 0 }}>
      <TableContainer
        sx={{
          border: 'none',
          width: '100%',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        component={Paper}
      >
        <Table sx={{ border: 'none' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FA' }}>
              <TableCell
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#181D25',
                  borderBottom: '2px solid #E0E0E0',
                  minWidth: '180px',
                }}
              >
                Customer Name
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#181D25',
                  borderBottom: '2px solid #E0E0E0',
                  minWidth: '150px',
                }}
              >
                Tag Admin
              </TableCell>
              <SortableHeader label="Shipments" active={customerShipmentSort} onClick={handleShipmentSort} />
              <SortableHeader
                label={`Weight ${name === 'Cargo Solutions Network' ? '(LBS)' : '(KG)'}`}
                active={customerWeightSort}
                onClick={handleWeightSort}
              />
              <SortableHeader
                label={`Revenue (${defaultOrgCountryCurrency})`}
                active={customerRevenueSort}
                onClick={handleRevenueSort}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((customer, index) => (
              <TableRow
                key={customer._id}
                onMouseEnter={() => setHoveredRow(customer._id)}
                onMouseLeave={() => setHoveredRow(null)}
                sx={{
                  transition: 'all 0.2s',
                  bgcolor: hoveredRow === customer._id ? '#F8F9FA' : 'transparent',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    transform: 'scale(1.01)',
                  },
                  borderLeft: hoveredRow === customer._id ? '3px solid #1976D2' : '3px solid transparent',
                }}
              >
                <TableCell
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: '15px',
                    color: '#181D25',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '180px',
                    borderBottom: '1px solid #F0F0F0',
                  }}
                >
                  <Tooltip title={customer?.name || 'N/A'} arrow placement="top">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: `hsl(${index * 40}, 70%, 85%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: '14px',
                          color: '#181D25',
                          flexShrink: 0,
                        }}
                      >
                        {customer?.name?.charAt(0).toUpperCase() || 'N'}
                      </Box>
                      <span>{customer?.name || 'N/A'}</span>
                    </Box>
                  </Tooltip>
                </TableCell>

                <TableCell
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: '14px',
                    color: '#606E80',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '150px',
                    borderBottom: '1px solid #F0F0F0',
                  }}
                >
                  <Tooltip title={customer._belongToAdminId?.name || 'N/A'} arrow>
                    <span>{customer._belongToAdminId?.name || 'N/A'}</span>
                  </Tooltip>
                </TableCell>

                <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid #F0F0F0' }}>
                  <MetricCard value={customer.totalShipment} percentage={customer.shipmentPercentage} />
                </TableCell>

                <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid #F0F0F0' }}>
                  <MetricCard
                    value={
                      name === 'Cargo Solutions Network'
                        ? convertWeightKGtoLB(customer.totalWeight)
                        : customer.totalWeight
                    }
                    percentage={customer.weightPercentage}
                  />
                </TableCell>

                <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid #F0F0F0' }}>
                  <MetricCard value={customer.totalRevenue} percentage={customer.revenuePercentage} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TopTenViewTable;
